import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { X, ChevronLeft, ChevronRight, Expand } from "lucide-react";
import { Reveal, SectionLabel, staggerContainer, staggerItem } from "../../lib/motion";
import { AMENITIES, COMMUNITY_INTRO, COMMUNITY_BANNER } from "../../data";

const LIGHTBOX_IMAGES = [
  { url: COMMUNITY_BANNER, title: "Bay Quarter Shores — Aerial" },
  ...AMENITIES.map((a) => ({ url: a.img, title: a.title })),
];

export const Community = () => {
  const [index, setIndex] = useState(null);
  const open = index !== null;

  const close = useCallback(() => setIndex(null), []);
  const next = useCallback(() => setIndex((i) => (i + 1) % LIGHTBOX_IMAGES.length), []);
  const prev = useCallback(() => setIndex((i) => (i - 1 + LIGHTBOX_IMAGES.length) % LIGHTBOX_IMAGES.length), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, close, next, prev]);

  return (
    <section id="community" className="py-24 md:py-32 bg-white scroll-mt-20">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Reveal className="max-w-3xl">
          <SectionLabel>Community Amenities</SectionLabel>
          <h2 className="font-serif text-4xl md:text-5xl font-light text-navy mt-4 leading-tight">
            A private community built around the water
          </h2>
          <p className="mt-6 text-warm leading-relaxed text-lg">{COMMUNITY_INTRO}</p>
        </Reveal>

        <Reveal delay={0.1}>
          <button
            type="button"
            data-testid="community-banner"
            onClick={() => setIndex(0)}
            className="group mt-12 block w-full relative rounded-3xl overflow-hidden shadow-[0_24px_70px_-28px_rgba(11,37,69,0.3)] focus-visible:ring-2 focus-visible:ring-water focus-visible:outline-none"
          >
            <img
              src={COMMUNITY_BANNER}
              alt="Bay Quarter Shores waterfront community aerial showing the clubhouse, pool and marina"
              loading="lazy"
              decoding="async"
              className="w-full h-[300px] md:h-[440px] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/25 to-transparent" />
            <span className="absolute bottom-4 right-4 flex items-center gap-1.5 bg-white/90 backdrop-blur text-navy text-xs font-medium rounded-full px-3 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <Expand size={13} /> View
            </span>
          </button>
        </Reveal>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {AMENITIES.map((a, i) => {
            const Icon = Icons[a.icon] || Icons.Circle;
            return (
              <motion.article
                key={a.title}
                variants={staggerItem}
                data-testid={`amenity-card-${a.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                className="group bg-white border border-warm-border rounded-3xl overflow-hidden shadow-sm transition duration-300 hover:-translate-y-1.5 hover:shadow-xl flex flex-col"
              >
                <button
                  type="button"
                  onClick={() => setIndex(i + 1)}
                  data-testid={`amenity-image-${a.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                  className="relative h-52 overflow-hidden focus-visible:ring-2 focus-visible:ring-water focus-visible:outline-none"
                  aria-label={`View ${a.title} image`}
                >
                  <img
                    src={a.img}
                    alt={a.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/45 to-transparent" />
                  <div className="absolute top-4 left-4 w-11 h-11 rounded-xl bg-white/90 backdrop-blur flex items-center justify-center shadow-sm">
                    <Icon size={20} strokeWidth={1.5} className="text-forest" />
                  </div>
                  <span className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-white/90 backdrop-blur text-navy text-xs font-medium rounded-full px-2.5 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Expand size={12} /> View
                  </span>
                </button>
                <div className="p-6 flex-1">
                  <h3 className="font-serif text-2xl text-navy leading-snug">{a.title}</h3>
                  <p className="mt-3 text-warm leading-relaxed text-sm">{a.desc}</p>
                </div>
              </motion.article>
            );
          })}
        </motion.div>
      </div>

      {open && (
          <motion.div
            data-testid="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] bg-navy-dark/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
            onClick={close}
          >
            <button
              data-testid="lightbox-close"
              onClick={close}
              className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              aria-label="Close"
            >
              <X size={22} />
            </button>

            <button
              data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-4 md:left-8 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              aria-label="Previous image"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-4 md:right-8 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              aria-label="Next image"
            >
              <ChevronRight size={24} />
            </button>

            <motion.figure
              key={index}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={LIGHTBOX_IMAGES[index].url}
                alt={LIGHTBOX_IMAGES[index].title}
                className="w-full max-h-[80vh] object-contain rounded-2xl"
              />
              <figcaption className="mt-4 text-center text-white/80 text-sm">
                {LIGHTBOX_IMAGES[index].title}
                <span className="text-white/40"> · {index + 1} / {LIGHTBOX_IMAGES.length}</span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
    </section>
  );
};
